import {Component, ElementRef, EventEmitter, Inject, ViewChild} from '@angular/core';
import {CarService} from "../../../services/car.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {
  AbstractProdForm,
  ProdFormButton,
  ProdFormField,
  ProdFormFieldType,
  ProdFormValidators,
  RequiredValidator,
  UniqueEntityValidator
} from 'prod-form';
import {IProdMap, ProdMarker} from 'prod-map';
import {ProdDialogData, ProdDialogService, ProdDialogType} from 'prod-dialog';
import {CarMapTemplate} from "../../../dip/map/template/car-map-template";
import {Car} from "../../../models/car";
import {Equipment} from "../../../models/equipment";
import {NationalityService} from "../../../services/nationality.service";
import {OrganizationTreeSelect} from "../../../dip/organization-tree-select";
import {RoleService} from "../../../services/role.service";
import {Role} from "../../../dip/role";
import {Subscription, take} from "rxjs";
import {FormEvent, SelectedRecords} from 'prod-grid';
import {CompanyService} from "../../../services/company.service";
import {ChangeCompanyDialogComponent} from "./change-company-dialog/change-company-dialog.component";
import {AuthenticationService} from "../../../services/authentication.service";
import {BarcodeScannerService} from "../../../services/barcode-scanner.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-car-form',
  templateUrl: './car-form.component.html'
})
export class CarFormComponent extends AbstractProdForm implements IProdMap {
  title: string = this.translate.instant('vehicle.form.title');
  mapTitle: string = this.translate.instant('vehicle.form.map_title');
  mapName: string = 'carMap';
  private isFieldsEditable: boolean = this.roleService.hasRole([Role.ROLE_CAR_EDIT]);
  markers$: EventEmitter<Map<number, ProdMarker>> = new EventEmitter<Map<number, ProdMarker>>();
  static carId: number;
  form;
  fields: ProdFormField[] = [
    ProdFormField.create({
      name: 'vin',
      label: this.translate.instant('vehicle.form.vin'),
      type: ProdFormFieldType.SELECT,
      service: CarService,
      selectParameters: {
        selectDataService: CarService,
        isMulti: false,
        isNarrowDown: true,
        isComplete: true
      },
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
      isMultiEditable: false,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'numberPlate',
      label: this.translate.instant('vehicle.form.number_plate'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'vhmdId_name',
      label: this.translate.instant('vehicle.form.vhmd_id'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable,
      isMultiEditable: false,
      afterElement: this.getScannerButton()
    }),
    ProdFormField.create({
      name: 'equipment_organization',
      label: this.translate.instant('vehicle.form.organization'),
      type: ProdFormFieldType.SELECT,
      selectParameters: {
        selectDataService: CompanyService,
        isMulti: false,
        isNarrowDown: true,
        isComplete: true
      },
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
      isEditable: false
    }),
    ProdFormField.create({
      name: 'nationality',
      label: this.translate.instant('vehicle.form.nationality'),
      type: ProdFormFieldType.SELECT,
      selectParameters: {
        selectDataService: NationalityService,
        isMulti: false,
        isNarrowDown: true,
        isComplete: true
      },
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'equipment_latitude',
      label: this.translate.instant('vehicle.form.latitude'),
      type: ProdFormFieldType.NUMBER,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'equipment_longitude',
      label: this.translate.instant('vehicle.form.longitude'),
      type: ProdFormFieldType.NUMBER,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'brand',
      label: this.translate.instant('vehicle.form.brand'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'model',
      label: this.translate.instant('vehicle.form.model'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'color',
      label: this.translate.instant('vehicle.form.color'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'parkingSpotNumber',
      label: this.translate.instant('vehicle.form.parking_spot'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    })
  ];
  formButtons: ProdFormButton[] = [];
  confirmReplaceDongleSubscription: Subscription;
  scanSuccessSubscriber: Subscription;

  constructor(private dialogRef: MatDialogRef<CarFormComponent>,
              @Inject(MAT_DIALOG_DATA) public dialogData: ProdDialogData,
              override dataService: CarService,
              private roleService: RoleService,
              private organizationTreeSelect: OrganizationTreeSelect,
              private dialogService: ProdDialogService,
              private authService: AuthenticationService,
              public barcodeScannerService: BarcodeScannerService,
              private translate: TranslateService) {
    super();
  }

  ngOnInit() {
    if (this.data.records) {
      this.setEditForm();
    } else if (this.organizationTreeSelect.company) {
        this.fields.find(f => f.name === 'equipment_organization').value = this.organizationTreeSelect.company.id;
    }

    this.confirmReplaceDongleSubscription = this.dataService.confirmReplaceDongle$.subscribe(value => {
      if (value) {
        this.dataService.confirmReplaceDongle$.next(null);
        const exception = value.exception;
        const request = value.request;

        this.dialogService.confirm(this.translate.instant('vehicle.form.confirm_replace_dongle', {vin1: exception.data, vin2: request.body.vin}))
          .subscribe(confirm => {
            if (confirm === true) {
              let updatedBody = request.body;
              updatedBody['force'] = true;
              if (request.method === 'PATCH') {
                const id = request.url.split('/').pop();
                this.dataService.prodGridPatch(updatedBody, +id).subscribe(res => {
                  if (res['success']) this.data.formResponseEvent.emit(new FormEvent());
                });
              }
              if (request.method == 'POST') {
                this.dataService.prodGridAdd(updatedBody).subscribe(res => {
                  if (res['success']) this.data.formResponseEvent.emit(new FormEvent());
                });
              }
            }
          });
      }
    })
    this.scanSuccessSubscriber = this.barcodeScannerService.scanSuccess$.subscribe(barcode => this.onScanSuccess(barcode));
  }

  setEditForm() {
    if (this.authService.currentUserValue.companies.length !== 1) {
      this.formButtons.push(new ProdFormButton({
        id: 'move-car-button',
        text: this.translate.instant('vehicle.form.move_car_button'),
        clickEvent: this.changeCompany.bind(this)
      }))
    }
    CarFormComponent.carId = Array.from(this.data.records.getAll())[0][0];
    this.fields.forEach(field => {
      if (field.name === 'equipment_organization') {
        field.name = 'equipment_organization_name'
      }
      if (field.name === 'vin') {
        field.type = ProdFormFieldType.UNIQUE_ENTITY;
        field.validators = new ProdFormValidators([
          new RequiredValidator(),
          new UniqueEntityValidator(this.dataService)
        ])
      }
    })
    let reactiveButtonAdded = false;
    this.data.records.getAll().forEach(record => {
      if (record['equipment'].isDeleted) {
        this.fields.push(ProdFormField.create({
          name: 'status',
          label: this.translate.instant('vehicle.form.status'),
          type: ProdFormFieldType.TEXT,
          value: 'deleted',
          isEditable: false
        }))
        this.fields.forEach(field => field.isEditable = false);
        const moveCarButton = this.formButtons.find(button => button.id === 'move-car-button');
        if (moveCarButton) moveCarButton.disabled = true;
        if (!reactiveButtonAdded) {
          this.formButtons.push(
            new ProdFormButton({
              id: 'reactivate-car-button',
              text: this.translate.instant('vehicle.form.reactivate_button'),
              clickEvent: this.reactiveCar.bind(this)
            })
          );
          reactiveButtonAdded = true;
        }
      }
    })
  }

  selectValueEvent(event) {
    if (!this.data.records && event.field === 'vin') {
      this.dataService.getCar(event.values).subscribe(res => {
        if (res.success) {
          this.data.records = new SelectedRecords();
          this.data.records.toggle(res.data);
          this.setEditForm();
          this.dialogRef.updateSize('60%', '80%')
        }
      });
    }
  }

  ngOnDestroy() {
    this.confirmReplaceDongleSubscription.unsubscribe();
    this.scanSuccessSubscriber.unsubscribe();
  }

  onTabChanged(event) {
    if (event.index === 1) {
      const markers: Map<number, ProdMarker> = new Map();
      if (this.dialogData.data.records) {
        this.dialogData.data.records.getAll().forEach(vehicle => {
          markers.set(vehicle['id'], new ProdMarker({
              id: vehicle['id'],
              name: vehicle['name'],
              lat: vehicle['equipment']['latitude'],
              lng: vehicle['equipment']['longitude'],
              icon: CarMapTemplate.getIcon(vehicle),
              tooltip: CarMapTemplate.getTooltip(vehicle),
              popup: CarMapTemplate.getPopup(vehicle),
              pulse: false,
              radius: 10
            }
          ));
        });
      } else {
        markers.set(0, this.getDummyVehicleMarker());
      }
      this.markers$.emit(markers);
    }
  }

  getScannerButton() {
    let scannerButton = document.createElement('button');
    scannerButton.setAttribute('style',
      'margin-left: 3px; ' +
      'border: none; ' +
      'width: 30px; ' +
      'color: black; ' +
      'height: 29px; ' +
      'font-size: initial; ' +
      'border: 1px solid #ced4da;' +
      'border-radius: 0.25rem;'
    );
    scannerButton.innerHTML = `<i class="fa fa-camera" style="display: flex; justify-content: center"></i>`;
    scannerButton.addEventListener('click', this.barcodeScannerService.openScanner.bind(this));
    return scannerButton;
  }

  private getDummyVehicleMarker(): ProdMarker {
    const car = new Car({
      id: 0,
      vin: 'vin',
      numberPlate: 'AAA000',
      equipment: new Equipment()
    });

    let lat = 49;
    let lng = 19;
    if (this.organizationTreeSelect.site) {
      lat = this.organizationTreeSelect.site.latitude;
      lng = this.organizationTreeSelect.site.longitude;
    }

    return new ProdMarker({
        id: 0,
        name: '',
        radius: 0,
        pulse: false,
        lng: lng,
        lat: lat,
        icon: CarMapTemplate.getIcon(car['data']),
        tooltip: '',
        popup: ''
      }
    );
  }

  changeCompany() {
    let cars = [];
    this.data.records['selectedRecords'].forEach(r => {
      cars.push(r.id);
    })
    this.dialogService.open(new ProdDialogData({
      component: ChangeCompanyDialogComponent,
      type: ProdDialogType.FORM,
      data: {cars: cars}
    }))
  }

  reactiveCar() {
    this.dialogService.confirm(this.translate.instant('vehicle.form.confirm_reactivate_vehicle')).subscribe(closed => {
      if (closed === true) {
        this.data.records['selectedRecords'].forEach(r => {
          if (r['equipment'].isDeleted) {
            this.dataService.reActivate(r['id']).pipe(take(1)).subscribe((res) => {
              if (res['success']) this.data.formResponseEvent.emit(new FormEvent());
            });
          }
        });
      }
    })
  }


  onFormOutput(form) {
    this.form = form;
  }

  onScanSuccess(barcode) {
    if (this.data.records && this.data.records.getSize() > 1) return;
    this.form.get('vhmdId_name').setValue(barcode);
  }
}
